/**
 * MILEAGE TAB - Company Operations
 * 
 * Tactical dark theme mileage tracking system.
 * NO ICONS - Text labels only per user requirements.
 * 
 * Role-based views:
 * - Employee: Clock in/out, trip logging, gas fillups, personal stats
 * - Admin/Owner: Fleet overview, red flags review, vehicle management, reports
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { usePermissions } from '../../contexts/PermissionsContext';
import RouteMapView from './RouteMapView';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// ═══════════════════════════════════════════════════════════════════════════════
// STYLES - Inline for tactical theme with NO ICONS
// ═══════════════════════════════════════════════════════════════════════════════

const styles = {
  // Colors
  colors: {
    background: '#020617',
    cardBg: '#0f172a',
    greenPrimary: '#10b981',
    greenGlow: '0 0 20px rgba(16, 185, 129, 0.3)',
    greenBorder: 'rgba(16, 185, 129, 0.5)',
    text: '#e2e8f0',
    textMuted: '#94a3b8',
    textDim: '#64748b',
    red: '#ef4444',
    amber: '#f59e0b',
    border: 'rgba(255, 255, 255, 0.1)',
  },
  // Card with green left border
  tacticalCard: {
    backgroundColor: '#0f172a',
    borderLeft: '3px solid #10b981',
    borderRadius: '8px',
    padding: '20px',
  },
  // Stat number with glow
  statNumber: {
    fontFamily: "'Rajdhani', sans-serif",
    fontSize: '2rem',
    fontWeight: 'bold',
    color: '#10b981',
    textShadow: '0 0 20px rgba(16, 185, 129, 0.4)',
  },
  // Money display
  moneyText: {
    fontFamily: "'JetBrains Mono', monospace",
    fontWeight: 'bold',
  },
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

const MileageTab = ({ user }) => {
  // Use PermissionsContext for reliable role detection
  const { userRole } = usePermissions();
  
  const [activeSubtab, setActiveSubtab] = useState('my-mileage');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Clock status
  const [clockStatus, setClockStatus] = useState(null);
  
  // Data
  const [summary, setSummary] = useState(null);
  const [trips, setTrips] = useState([]);
  const [fillups, setFillups] = useState([]);
  const [flags, setFlags] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [liveStatus, setLiveStatus] = useState(null);
  const [allEmployees, setAllEmployees] = useState([]);
  
  // Notifications
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);

  // Check if user is admin/owner/manager - use userRole from context (fallback to prop)
  const effectiveRole = userRole || user?.role;
  const isAdmin = ['owner', 'admin'].includes(effectiveRole);
  const isManager = ['owner', 'admin', 'manager'].includes(effectiveRole);

  // Define subtabs based on role - memoized to update when role changes
  const SUBTABS = useMemo(() => {
    const baseTabs = [
      { id: 'my-mileage', label: 'MY MILEAGE' },
    ];
    
    if (isManager) {
      baseTabs.push({ id: 'fleet-overview', label: 'FLEET OVERVIEW' });
      baseTabs.push({ id: 'red-flags', label: 'RED FLAGS' });
    }
    
    if (isAdmin) {
      baseTabs.push({ id: 'vehicles', label: 'VEHICLES' });
      baseTabs.push({ id: 'rules', label: 'RULES' });
      baseTabs.push({ id: 'reports', label: 'REPORTS' });
    }
    
    return baseTabs;
  }, [isAdmin, isManager]);

  // Fetch helpers
  const fetchWithAuth = useCallback(async (url, options = {}) => {
    const response = await fetch(`${API_BASE}${url}`, {
      credentials: 'include',
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(err.detail || 'Request failed');
    }
    return response.json();
  }, []);

  // Load clock status
  const loadClockStatus = useCallback(async () => {
    try {
      const data = await fetchWithAuth('/api/mileage/clock-status');
      setClockStatus(data);
    } catch (err) {
      console.error('Failed to load clock status:', err);
    }
  }, [fetchWithAuth]);

  // Load summary
  const loadSummary = useCallback(async () => {
    try {
      const data = await fetchWithAuth('/api/mileage/reports/summary');
      setSummary(data);
    } catch (err) {
      console.error('Failed to load summary:', err);
    }
  }, [fetchWithAuth]);

  // Load trips
  const loadTrips = useCallback(async () => {
    try {
      const data = await fetchWithAuth('/api/mileage/trips?limit=50');
      setTrips(data.trips || []);
    } catch (err) {
      console.error('Failed to load trips:', err);
    }
  }, [fetchWithAuth]);

  // Load fillups
  const loadFillups = useCallback(async () => {
    try {
      const data = await fetchWithAuth('/api/mileage/fillups?limit=50');
      setFillups(data.fillups || []);
    } catch (err) {
      console.error('Failed to load fillups:', err);
    }
  }, [fetchWithAuth]);

  // Load flags
  const loadFlags = useCallback(async () => {
    try {
      const data = await fetchWithAuth('/api/mileage/flags?limit=50');
      setFlags(data.flags || []);
    } catch (err) {
      console.error('Failed to load flags:', err);
    }
  }, [fetchWithAuth]);

  // Load vehicles
  const loadVehicles = useCallback(async () => {
    try {
      const data = await fetchWithAuth('/api/mileage/vehicles');
      setVehicles(data.vehicles || []);
    } catch (err) {
      console.error('Failed to load vehicles:', err);
    }
  }, [fetchWithAuth]);

  // Load live status
  const loadLiveStatus = useCallback(async () => {
    try {
      const data = await fetchWithAuth('/api/mileage/admin/live-status');
      setLiveStatus(data);
    } catch (err) {
      console.error('Failed to load live status:', err);
    }
  }, [fetchWithAuth]);

  // Load all employees
  const loadAllEmployees = useCallback(async () => {
    try {
      const data = await fetchWithAuth('/api/mileage/admin/all-employees');
      setAllEmployees(data.employees || []);
    } catch (err) {
      console.error('Failed to load employees:', err);
    }
  }, [fetchWithAuth]);

  // Load notifications
  const loadNotifications = useCallback(async () => {
    try {
      const data = await fetchWithAuth('/api/mileage/notifications?limit=20');
      setNotifications(data.notifications || []);
    } catch (err) {
      console.error('Failed to load notifications:', err);
    }
  }, [fetchWithAuth]);

  // Mark notification as read
  const markNotificationRead = async (notificationId) => {
    try {
      await fetchWithAuth(`/api/mileage/notifications/${notificationId}/read`, { method: 'POST' });
      loadNotifications();
    } catch (err) {
      console.error('Failed to mark notification read:', err);
    }
  };

  // Mark all notifications as read
  const markAllRead = async () => {
    try {
      await fetchWithAuth('/api/mileage/notifications/read-all', { method: 'POST' });
      loadNotifications();
    } catch (err) {
      console.error('Failed to mark all read:', err);
    }
  };

  // Initial load
  useEffect(() => {
    loadClockStatus();
    loadSummary();
    loadTrips();
    loadFillups();
    loadNotifications();
    
    // Refresh clock status every 30 seconds
    const interval = setInterval(loadClockStatus, 30000);
    // Refresh notifications every minute
    const notifInterval = setInterval(loadNotifications, 60000);
    return () => {
      clearInterval(interval);
      clearInterval(notifInterval);
    };
  }, [loadClockStatus, loadSummary, loadTrips, loadFillups, loadNotifications]);

  // Load data based on active tab
  useEffect(() => {
    if (activeSubtab === 'fleet-overview') {
      loadLiveStatus();
      loadAllEmployees();
    }
    if (activeSubtab === 'red-flags') loadFlags();
    if (activeSubtab === 'vehicles') loadVehicles();
    if (activeSubtab === 'reports') loadSummary();
  }, [activeSubtab, loadLiveStatus, loadAllEmployees, loadFlags, loadVehicles, loadSummary]);

  // Navigate to Duty Log for clock in/out
  const goToDutyLog = () => {
    window.location.href = '/time-tracking';
  };

  // Render subtab content
  const renderContent = () => {
    switch (activeSubtab) {
      case 'my-mileage':
        return (
          <MyMileageView 
            clockStatus={clockStatus}
            goToDutyLog={goToDutyLog}
            loading={loading}
            trips={trips}
            fillups={fillups}
            summary={summary}
            fetchWithAuth={fetchWithAuth}
            onRefreshTrips={loadTrips}
            onRefreshFillups={loadFillups}
          />
        );
      case 'fleet-overview':
        return (
          <FleetOverviewView 
            liveStatus={liveStatus}
            allEmployees={allEmployees}
            summary={summary}
            flags={flags}
            onRefresh={() => { loadLiveStatus(); loadAllEmployees(); }}
          />
        );
      case 'red-flags':
        return (
          <RedFlagsView 
            flags={flags}
            onRefresh={loadFlags}
            fetchWithAuth={fetchWithAuth}
            canApprove={isAdmin}
          />
        );
      case 'vehicles':
        return (
          <VehiclesView 
            vehicles={vehicles}
            onRefresh={loadVehicles}
            fetchWithAuth={fetchWithAuth}
          />
        );
      case 'rules':
        return (
          <MileageRulesView 
            fetchWithAuth={fetchWithAuth}
          />
        );
      case 'reports':
        return (
          <ReportsView 
            summary={summary}
            fetchWithAuth={fetchWithAuth}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6" data-testid="mileage-tab">
      {/* Clock Status Banner - Always visible */}
      {clockStatus && (
        <div 
          style={{
            ...styles.tacticalCard,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderLeftColor: clockStatus.is_clocked_in ? '#10b981' : '#64748b',
          }}
          data-testid="clock-status-banner"
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div 
              style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                backgroundColor: clockStatus.is_clocked_in ? '#10b981' : '#64748b',
                boxShadow: clockStatus.is_clocked_in ? '0 0 10px rgba(16, 185, 129, 0.5)' : 'none',
                animation: clockStatus.is_clocked_in ? 'pulse 2s infinite' : 'none',
              }}
            />
            <div>
              <span style={{ color: '#e2e8f0', fontWeight: '600', fontSize: '1rem' }}>
                {clockStatus.is_clocked_in 
                  ? `CLOCKED IN: ${clockStatus.hours_worked?.toFixed(1)} HOURS`
                  : 'NOT CLOCKED IN'}
              </span>
              {clockStatus.vehicle_id && (
                <span style={{ color: '#10b981', fontSize: '0.875rem', marginLeft: '12px' }}>
                  Vehicle assigned
                </span>
              )}
              {clockStatus.warning && (
                <span style={{ color: '#f59e0b', fontSize: '0.875rem', marginLeft: '12px' }}>
                  {clockStatus.warning}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={goToDutyLog}
            style={{
              padding: '12px 24px',
              borderRadius: '6px',
              fontWeight: 'bold',
              fontSize: '0.875rem',
              letterSpacing: '0.05em',
              border: 'none',
              cursor: 'pointer',
              backgroundColor: clockStatus.is_clocked_in ? 'rgba(100, 116, 139, 0.2)' : '#10b981',
              color: clockStatus.is_clocked_in ? '#94a3b8' : '#ffffff',
            }}
            data-testid="go-to-duty-log-btn"
          >
            {clockStatus.is_clocked_in ? 'VIEW DUTY LOG' : 'GO TO DUTY LOG'}
          </button>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div style={{
          padding: '16px',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '8px',
          color: '#ef4444',
        }}>
          {error}
        </div>
      )}

      {/* Subtab Navigation - NO ICONS, text only */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
        {SUBTABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveSubtab(tab.id)}
            style={{
              padding: '10px 20px',
              borderRadius: '6px',
              fontWeight: '600',
              fontSize: '0.75rem',
              letterSpacing: '0.1em',
              border: activeSubtab === tab.id ? '1px solid rgba(16, 185, 129, 0.5)' : '1px solid rgba(255, 255, 255, 0.1)',
              backgroundColor: activeSubtab === tab.id ? 'rgba(16, 185, 129, 0.15)' : '#0f172a',
              color: activeSubtab === tab.id ? '#10b981' : '#94a3b8',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            data-testid={`mileage-subtab-${tab.id}`}
          >
            {tab.label}
            {tab.id === 'red-flags' && flags.filter(f => f.status === 'pending_review').length > 0 && (
              <span style={{
                marginLeft: '8px',
                backgroundColor: '#ef4444',
                color: '#fff',
                padding: '2px 8px',
                borderRadius: '10px',
                fontSize: '0.7rem',
              }}>
                {flags.filter(f => f.status === 'pending_review').length}
              </span>
            )}
          </button>
        ))}
        
        {/* Notifications Button - spacer to push to right */}
        <div style={{ flex: 1 }} />
        <button
          onClick={() => setShowNotifications(!showNotifications)}
          style={{
            padding: '10px 16px',
            borderRadius: '6px',
            fontWeight: '600',
            fontSize: '0.75rem',
            letterSpacing: '0.1em',
            border: notifications.filter(n => !n.read).length > 0 
              ? '1px solid rgba(245, 158, 11, 0.5)' 
              : '1px solid rgba(255, 255, 255, 0.1)',
            backgroundColor: notifications.filter(n => !n.read).length > 0 
              ? 'rgba(245, 158, 11, 0.15)' 
              : '#0f172a',
            color: notifications.filter(n => !n.read).length > 0 ? '#f59e0b' : '#94a3b8',
            cursor: 'pointer',
          }}
          data-testid="notifications-btn"
        >
          ALERTS
          {notifications.filter(n => !n.read).length > 0 && (
            <span style={{
              marginLeft: '8px',
              backgroundColor: '#f59e0b',
              color: '#000',
              padding: '2px 8px',
              borderRadius: '10px',
              fontSize: '0.7rem',
              fontWeight: 'bold',
            }}>
              {notifications.filter(n => !n.read).length}
            </span>
          )}
        </button>
      </div>

      {/* Notifications Panel */}
      {showNotifications && (
        <div style={{
          ...styles.tacticalCard,
          borderLeftColor: '#f59e0b',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <span style={{ fontWeight: 'bold', color: '#e2e8f0', letterSpacing: '0.05em' }}>
              NOTIFICATIONS
            </span>
            {notifications.filter(n => !n.read).length > 0 && (
              <button onClick={markAllRead} style={refreshBtnStyle}>
                MARK ALL READ
              </button>
            )}
          </div>
          
          {notifications.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px', color: '#64748b' }}>
              No notifications
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '300px', overflowY: 'auto' }}>
              {notifications.map(notif => (
                <div
                  key={notif.notification_id}
                  onClick={() => !notif.read && markNotificationRead(notif.notification_id)}
                  style={{
                    padding: '12px 16px',
                    backgroundColor: notif.read ? '#020617' : 'rgba(245, 158, 11, 0.1)',
                    borderRadius: '6px',
                    borderLeft: `3px solid ${
                      notif.severity === 'warning' ? '#f59e0b' :
                      notif.severity === 'error' ? '#ef4444' : '#10b981'
                    }`,
                    cursor: notif.read ? 'default' : 'pointer',
                    opacity: notif.read ? 0.6 : 1,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ color: '#e2e8f0', fontWeight: '500', marginBottom: '4px' }}>
                        {notif.title}
                      </div>
                      <div style={{ color: '#94a3b8', fontSize: '0.875rem' }}>
                        {notif.message}
                      </div>
                    </div>
                    <div style={{ color: '#64748b', fontSize: '0.75rem', whiteSpace: 'nowrap', marginLeft: '12px' }}>
                      {notif.created_at ? new Date(notif.created_at).toLocaleDateString() : ''}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Content */}
      {renderContent()}

      {/* CSS for pulse animation */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @import url('https://fonts.googleapis.com/css2?family=Rajdhani:wght@500;600;700&family=JetBrains+Mono:wght@500;600&display=swap');
      `}</style>
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════════════════════
// MY MILEAGE VIEW (Employee View)
// ═══════════════════════════════════════════════════════════════════════════════

const MyMileageView = ({ 
  clockStatus, 
  goToDutyLog,
  loading, 
  trips, 
  fillups, 
  summary,
  fetchWithAuth,
  onRefreshTrips,
  onRefreshFillups 
}) => {
  const [showTripForm, setShowTripForm] = useState(false);
  const [showFillupForm, setShowFillupForm] = useState(false);
  const [newTrip, setNewTrip] = useState({ destination_text: '', reason_text: '', miles_driven: '' });
  const [newFillup, setNewFillup] = useState({
    date: new Date().toISOString().split('T')[0],
    vendor: '',
    amount: '',
    gallons: '',
    odometer: ''
  });
  const [saving, setSaving] = useState(false);
  
  // GPS Tracking state
  const [gpsEnabled, setGpsEnabled] = useState(false);
  const [gpsStatus, setGpsStatus] = useState(null);
  const [trackingInterval, setTrackingInterval] = useState(null);
  
  // Route Map state
  const [showRouteMap, setShowRouteMap] = useState(false);
  const [selectedRouteDate, setSelectedRouteDate] = useState(new Date().toISOString().split('T')[0]);
  const [routeData, setRouteData] = useState(null);
  const [loadingRoute, setLoadingRoute] = useState(false);

  // Load route data for selected date
  const loadRouteForDate = async (date) => {
    setLoadingRoute(true);
    try {
      const data = await fetchWithAuth(`/api/mileage/gps/route/${date}`);
      setRouteData(data);
      setShowRouteMap(true);
    } catch (err) {
      console.error('Failed to load route:', err);
      setRouteData({ route_points: [], total_miles: 0 });
      setShowRouteMap(true);
    } finally {
      setLoadingRoute(false);
    }
  };

  // Load GPS tracking status
  useEffect(() => {
    const loadGpsStatus = async () => {
      try {
        const data = await fetchWithAuth('/api/mileage/gps/tracking-status');
        setGpsStatus(data);
        setGpsEnabled(data.gps_tracking_enabled || false);
      } catch (err) {
        console.error('Failed to load GPS status:', err);
      }
    };
    loadGpsStatus();
  }, [fetchWithAuth, clockStatus?.is_clocked_in]);

  // GPS tracking effect
  useEffect(() => {
    if (gpsEnabled && clockStatus?.is_clocked_in) {
      // Start GPS tracking
      const sendLocation = async () => {
        if (!navigator.geolocation) return;
        
        navigator.geolocation.getCurrentPosition(
          async (pos) => {
            try {
              await fetchWithAuth('/api/mileage/gps/location', {
                method: 'POST',
                body: JSON.stringify({
                  lat: pos.coords.latitude,
                  lng: pos.coords.longitude,
                  accuracy: pos.coords.accuracy,
                  speed: pos.coords.speed,
                  heading: pos.coords.heading,
                  altitude: pos.coords.altitude,
                  timestamp: new Date().toISOString()
                }),
              });
            } catch (err) {
              console.error('Failed to send location:', err);
            }
          },
          (err) => console.error('GPS error:', err),
          { enableHighAccuracy: true, timeout: 10000 }
        );
      };
      
      // Send location immediately
      sendLocation();
      
      // Then every 2 minutes
      const interval = setInterval(sendLocation, 120000);
      setTrackingInterval(interval);
      
      return () => {
        if (interval) clearInterval(interval);
      };
    } else {
      // Stop tracking
      if (trackingInterval) {
        clearInterval(trackingInterval);
        setTrackingInterval(null);
      }
    }
  }, [gpsEnabled, clockStatus?.is_clocked_in, fetchWithAuth]);

  const toggleGpsTracking = async () => {
    try {
      const newState = !gpsEnabled;
      await fetchWithAuth(`/api/mileage/gps/toggle-tracking?enabled=${newState}`, {
        method: 'POST',
      });
      setGpsEnabled(newState);
    } catch (err) {
      alert(err.message);
    }
  };

  // Calculate today's stats
  const today = new Date().toISOString().split('T')[0];
  const todayTrips = trips.filter(t => t.start_time?.startsWith(today));
  const todayMiles = todayTrips.reduce((sum, t) => sum + (t.miles_driven || 0), 0);
  
  // This month stats
  const thisMonth = new Date().toISOString().slice(0, 7);
  const monthFillups = fillups.filter(f => f.date?.startsWith(thisMonth));
  const monthGasSpend = monthFillups.reduce((sum, f) => sum + (f.amount || 0), 0);
  const monthGallons = monthFillups.reduce((sum, f) => sum + (f.gallons || 0), 0);

  const handleAddTrip = async (e) => {
    e.preventDefault();
    if (!clockStatus?.is_clocked_in) {
      alert('You must be clocked in to log trips');
      return;
    }
    
    setSaving(true);
    try {
      await fetchWithAuth('/api/mileage/trips', {
        method: 'POST',
        body: JSON.stringify({
          destination_text: newTrip.destination_text,
          reason_text: newTrip.reason_text,
          miles_driven: newTrip.miles_driven ? parseFloat(newTrip.miles_driven) : null
        }),
      });
      
      setNewTrip({ destination_text: '', reason_text: '', miles_driven: '' });
      setShowTripForm(false);
      onRefreshTrips();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAddFillup = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await fetchWithAuth('/api/mileage/fillups', {
        method: 'POST',
        body: JSON.stringify({
          date: newFillup.date,
          vendor: newFillup.vendor,
          amount: parseFloat(newFillup.amount),
          gallons: newFillup.gallons ? parseFloat(newFillup.gallons) : null,
          odometer: newFillup.odometer ? parseInt(newFillup.odometer) : null
        }),
      });
      
      setNewFillup({
        date: new Date().toISOString().split('T')[0],
        vendor: '',
        amount: '',
        gallons: '',
        odometer: ''
      });
      setShowFillupForm(false);
      onRefreshFillups();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Clock Status Display - Links to Duty Log */}
      <div style={{
        ...styles.tacticalCard,
        textAlign: 'center',
        padding: '40px',
      }}>
        <div style={{
          width: '140px',
          height: '140px',
          margin: '0 auto 24px',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: clockStatus?.is_clocked_in ? 'rgba(16, 185, 129, 0.15)' : 'rgba(100, 116, 139, 0.15)',
          border: `3px solid ${clockStatus?.is_clocked_in ? '#10b981' : '#64748b'}`,
          boxShadow: clockStatus?.is_clocked_in ? '0 0 30px rgba(16, 185, 129, 0.3)' : 'none',
        }}>
          <span style={{
            ...styles.statNumber,
            fontSize: '2.5rem',
            color: clockStatus?.is_clocked_in ? '#10b981' : '#64748b',
          }}>
            {clockStatus?.is_clocked_in ? clockStatus.hours_worked?.toFixed(1) : '0.0'}
          </span>
        </div>
        
        <div style={{ color: '#e2e8f0', fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '8px' }}>
          {clockStatus?.is_clocked_in ? 'HOURS ON CLOCK' : 'NOT CLOCKED IN'}
        </div>
        
        {clockStatus?.is_clocked_in && clockStatus?.clock_in_time && (
          <div style={{ color: '#94a3b8', fontSize: '0.875rem', marginBottom: '8px' }}>
            Since {new Date(clockStatus.clock_in_time).toLocaleTimeString()}
          </div>
        )}
        
        {clockStatus?.vehicle_id && (
          <div style={{ color: '#10b981', fontSize: '0.875rem', marginBottom: '16px' }}>
            Vehicle assigned to this shift
          </div>
        )}
        
        {!clockStatus?.is_clocked_in && (
          <div style={{ color: '#94a3b8', fontSize: '0.875rem', marginBottom: '16px' }}>
            Clock in via Duty Log to start tracking
          </div>
        )}
        
        <button
          onClick={goToDutyLog}
          style={{
            padding: '16px 48px',
            borderRadius: '8px',
            fontWeight: 'bold',
            fontSize: '1rem',
            letterSpacing: '0.1em',
            border: 'none',
            cursor: 'pointer',
            backgroundColor: clockStatus?.is_clocked_in ? 'rgba(100, 116, 139, 0.2)' : '#10b981',
            color: clockStatus?.is_clocked_in ? '#94a3b8' : '#ffffff',
            transition: 'all 0.2s',
          }}
          data-testid="duty-log-btn"
        >
          {clockStatus?.is_clocked_in ? 'VIEW DUTY LOG' : 'GO TO DUTY LOG TO CLOCK IN'}
        </button>
        
        {clockStatus?.warning && (
          <div style={{
            marginTop: '16px',
            padding: '12px',
            backgroundColor: 'rgba(245, 158, 11, 0.15)',
            borderRadius: '6px',
            color: '#f59e0b',
          }}>
            {clockStatus.warning}
          </div>
        )}
      </div>

      {/* GPS Tracking Control */}
      <div style={{
        ...styles.tacticalCard,
        borderLeftColor: gpsEnabled && clockStatus?.is_clocked_in ? '#10b981' : '#64748b',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ color: '#e2e8f0', fontWeight: 'bold', marginBottom: '4px' }}>
              GPS TRIP TRACKING
            </div>
            <div style={{ color: '#94a3b8', fontSize: '0.875rem' }}>
              {gpsEnabled && clockStatus?.is_clocked_in 
                ? `Auto-tracking enabled • ${gpsStatus?.locations_today || 0} points recorded today`
                : 'Enable to auto-log trips from GPS location'}
            </div>
            {gpsStatus?.last_location && (
              <div style={{ color: '#64748b', fontSize: '0.75rem', marginTop: '4px' }}>
                Last update: {gpsStatus.last_location.updated_at ? 
                  new Date(gpsStatus.last_location.updated_at).toLocaleTimeString() : 'Never'}
              </div>
            )}
          </div>
          <button
            onClick={toggleGpsTracking}
            disabled={!clockStatus?.is_clocked_in}
            style={{
              padding: '12px 24px',
              borderRadius: '6px',
              fontWeight: 'bold',
              fontSize: '0.875rem',
              letterSpacing: '0.05em',
              border: 'none',
              cursor: clockStatus?.is_clocked_in ? 'pointer' : 'not-allowed',
              opacity: clockStatus?.is_clocked_in ? 1 : 0.5,
              backgroundColor: gpsEnabled ? 'rgba(16, 185, 129, 0.2)' : 'rgba(100, 116, 139, 0.2)',
              color: gpsEnabled ? '#10b981' : '#94a3b8',
            }}
            data-testid="gps-tracking-toggle"
          >
            {gpsEnabled ? 'GPS ON' : 'GPS OFF'}
          </button>
        </div>
        
        {gpsEnabled && clockStatus?.is_clocked_in && (
          <div style={{
            marginTop: '12px',
            padding: '8px 12px',
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}>
            <div style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: '#10b981',
              animation: 'pulse 2s infinite',
            }} />
            <span style={{ color: '#10b981', fontSize: '0.875rem' }}>
              Recording location every 2 minutes
            </span>
          </div>
        )}
      </div>

      {/* Today's Stats Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
        <StatCard label="TODAY'S MILES" value={todayMiles.toFixed(0)} unit="mi" />
        <StatCard label="TODAY'S TRIPS" value={todayTrips.length} unit="" />
        <StatCard label="MONTH GAS" value={`$${monthGasSpend.toFixed(0)}`} unit="" isMoney />
        <StatCard label="MONTH GALLONS" value={monthGallons.toFixed(0)} unit="gal" />
      </div>

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '16px' }}>
        <button
          onClick={() => setShowTripForm(!showTripForm)}
          disabled={!clockStatus?.is_clocked_in}
          style={{
            flex: 1,
            padding: '16px',
            borderRadius: '8px',
            fontWeight: 'bold',
            fontSize: '0.875rem',
            letterSpacing: '0.05em',
            border: '1px solid rgba(16, 185, 129, 0.5)',
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
            color: clockStatus?.is_clocked_in ? '#10b981' : '#64748b',
            cursor: clockStatus?.is_clocked_in ? 'pointer' : 'not-allowed',
            opacity: clockStatus?.is_clocked_in ? 1 : 0.5,
          }}
          data-testid="log-trip-btn"
        >
          LOG TRIP
        </button>
        <button
          onClick={() => setShowFillupForm(!showFillupForm)}
          style={{
            flex: 1,
            padding: '16px',
            borderRadius: '8px',
            fontWeight: 'bold',
            fontSize: '0.875rem',
            letterSpacing: '0.05em',
            border: '1px solid rgba(245, 158, 11, 0.5)',
            backgroundColor: 'rgba(245, 158, 11, 0.1)',
            color: '#f59e0b',
            cursor: 'pointer',
          }}
          data-testid="log-gas-btn"
        >
          LOG GAS
        </button>
      </div>

      {/* Trip Form */}
      {showTripForm && (
        <form onSubmit={handleAddTrip} style={styles.tacticalCard}>
          <div style={{ marginBottom: '16px', fontWeight: 'bold', color: '#e2e8f0', letterSpacing: '0.05em' }}>
            NEW TRIP
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div>
              <label style={{ display: 'block', color: '#94a3b8', fontSize: '0.75rem', marginBottom: '4px', letterSpacing: '0.05em' }}>
                DESTINATION
              </label>
              <input
                type="text"
                value={newTrip.destination_text}
                onChange={(e) => setNewTrip({ ...newTrip, destination_text: e.target.value })}
                placeholder="e.g., Home Depot - 123 Main St"
                required
                style={inputStyle}
                data-testid="trip-destination-input"
              />
            </div>
            <div>
              <label style={{ display: 'block', color: '#94a3b8', fontSize: '0.75rem', marginBottom: '4px', letterSpacing: '0.05em' }}>
                REASON
              </label>
              <input
                type="text"
                value={newTrip.reason_text}
                onChange={(e) => setNewTrip({ ...newTrip, reason_text: e.target.value })}
                placeholder="e.g., Pick up materials for Johnson project"
                required
                style={inputStyle}
                data-testid="trip-reason-input"
              />
            </div>
            <div>
              <label style={{ display: 'block', color: '#94a3b8', fontSize: '0.75rem', marginBottom: '4px', letterSpacing: '0.05em' }}>
                MILES (OPTIONAL)
              </label>
              <input
                type="number"
                step="0.1"
                value={newTrip.miles_driven}
                onChange={(e) => setNewTrip({ ...newTrip, miles_driven: e.target.value })}
                placeholder="e.g., 12.5"
                style={inputStyle}
                data-testid="trip-miles-input"
              />
            </div>
            <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
              <button type="submit" disabled={saving} style={primaryBtnStyle} data-testid="save-trip-btn">
                {saving ? 'SAVING...' : 'SAVE TRIP'}
              </button>
              <button type="button" onClick={() => setShowTripForm(false)} style={secondaryBtnStyle}>
                CANCEL
              </button>
            </div>
          </div>
        </form>
      )}

      {/* Fillup Form */}
      {showFillupForm && (
        <form onSubmit={handleAddFillup} style={styles.tacticalCard}>
          <div style={{ marginBottom: '16px', fontWeight: 'bold', color: '#e2e8f0', letterSpacing: '0.05em' }}>
            NEW GAS FILLUP
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label style={{ display: 'block', color: '#94a3b8', fontSize: '0.75rem', marginBottom: '4px', letterSpacing: '0.05em' }}>
                DATE
              </label>
              <input
                type="date"
                value={newFillup.date}
                onChange={(e) => setNewFillup({ ...newFillup, date: e.target.value })}
                required
                style={inputStyle}
              />
            </div>
            <div>
              <label style={{ display: 'block', color: '#94a3b8', fontSize: '0.75rem', marginBottom: '4px', letterSpacing: '0.05em' }}>
                VENDOR
              </label>
              <input
                type="text"
                value={newFillup.vendor}
                onChange={(e) => setNewFillup({ ...newFillup, vendor: e.target.value })}
                placeholder="e.g., Shell, QuikTrip"
                required
                style={inputStyle}
                data-testid="fillup-vendor-input"
              />
            </div>
            <div>
              <label style={{ display: 'block', color: '#94a3b8', fontSize: '0.75rem', marginBottom: '4px', letterSpacing: '0.05em' }}>
                TOTAL AMOUNT ($)
              </label>
              <input
                type="number"
                step="0.01"
                value={newFillup.amount}
                onChange={(e) => setNewFillup({ ...newFillup, amount: e.target.value })}
                placeholder="e.g., 45.67"
                required
                style={inputStyle}
                data-testid="fillup-amount-input"
              />
            </div>
            <div>
              <label style={{ display: 'block', color: '#94a3b8', fontSize: '0.75rem', marginBottom: '4px', letterSpacing: '0.05em' }}>
                GALLONS
              </label>
              <input
                type="number"
                step="0.01"
                value={newFillup.gallons}
                onChange={(e) => setNewFillup({ ...newFillup, gallons: e.target.value })}
                placeholder="e.g., 12.5"
                style={inputStyle}
                data-testid="fillup-gallons-input"
              />
            </div>
            <div style={{ gridColumn: 'span 2' }}>
              <label style={{ display: 'block', color: '#94a3b8', fontSize: '0.75rem', marginBottom: '4px', letterSpacing: '0.05em' }}>
                ODOMETER READING
              </label>
              <input
                type="number"
                value={newFillup.odometer}
                onChange={(e) => setNewFillup({ ...newFillup, odometer: e.target.value })}
                placeholder="e.g., 45678"
                style={inputStyle}
                data-testid="fillup-odometer-input"
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
            <button type="submit" disabled={saving} style={primaryBtnStyle} data-testid="save-fillup-btn">
              {saving ? 'SAVING...' : 'SAVE FILLUP'}
            </button>
            <button type="button" onClick={() => setShowFillupForm(false)} style={secondaryBtnStyle}>
              CANCEL
            </button>
          </div>
        </form>
      )}

      {/* Trip History */}
      <div style={styles.tacticalCard}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <span style={{ fontWeight: 'bold', color: '#e2e8f0', letterSpacing: '0.05em' }}>TRIP HISTORY</span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={onRefreshTrips} style={refreshBtnStyle}>REFRESH</button>
          </div>
        </div>
        {trips.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '32px', color: '#64748b' }}>
            No trips logged yet
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {trips.slice(0, 10).map(trip => (
              <div key={trip.trip_id} style={{
                padding: '12px 16px',
                backgroundColor: '#020617',
                borderRadius: '6px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
              }}>
                <div>
                  <div style={{ color: '#e2e8f0', fontWeight: '500' }}>{trip.destination_text}</div>
                  <div style={{ color: '#64748b', fontSize: '0.875rem' }}>{trip.reason_text}</div>
                  {trip.source === 'gps_auto' && (
                    <span style={{
                      marginTop: '4px',
                      display: 'inline-block',
                      padding: '2px 6px',
                      backgroundColor: 'rgba(16, 185, 129, 0.2)',
                      color: '#10b981',
                      fontSize: '0.65rem',
                      borderRadius: '4px',
                    }}>
                      GPS AUTO
                    </span>
                  )}
                </div>
                <div style={{ textAlign: 'right' }}>
                  {trip.miles_driven && (
                    <div style={{ color: '#10b981', fontFamily: "'JetBrains Mono', monospace" }}>
                      {trip.miles_driven} mi
                    </div>
                  )}
                  <div style={{ color: '#64748b', fontSize: '0.75rem' }}>
                    {new Date(trip.start_time).toLocaleDateString()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Route Map Section */}
      <div style={styles.tacticalCard}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <span style={{ fontWeight: 'bold', color: '#e2e8f0', letterSpacing: '0.05em' }}>GPS ROUTE MAP</span>
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px' }}>
          <div>
            <label style={{ display: 'block', color: '#94a3b8', fontSize: '0.75rem', marginBottom: '4px', letterSpacing: '0.05em' }}>
              SELECT DATE
            </label>
            <input
              type="date"
              value={selectedRouteDate}
              onChange={(e) => setSelectedRouteDate(e.target.value)}
              style={{
                ...inputStyle,
                width: '180px',
              }}
            />
          </div>
          <button
            onClick={() => loadRouteForDate(selectedRouteDate)}
            disabled={loadingRoute}
            style={{
              ...primaryBtnStyle,
              marginTop: '20px',
              opacity: loadingRoute ? 0.5 : 1,
            }}
            data-testid="view-route-btn"
          >
            {loadingRoute ? 'LOADING...' : 'VIEW ROUTE'}
          </button>
          <button
            onClick={() => loadRouteForDate(new Date().toISOString().split('T')[0])}
            disabled={loadingRoute}
            style={{
              ...secondaryBtnStyle,
              marginTop: '20px',
            }}
          >
            TODAY
          </button>
        </div>
        
        {showRouteMap && routeData && (
          <RouteMapView
            routePoints={routeData.route_points || []}
            date={selectedRouteDate}
            totalMiles={routeData.total_miles || 0}
            onClose={() => setShowRouteMap(false)}
          />
        )}
        
        {!showRouteMap && (
          <div style={{ textAlign: 'center', padding: '24px', color: '#64748b', fontSize: '0.875rem' }}>
            Select a date and click VIEW ROUTE to see your GPS path on the map
          </div>
        )}
      </div>

      {/* Fillup History */}
      <div style={styles.tacticalCard}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <span style={{ fontWeight: 'bold', color: '#e2e8f0', letterSpacing: '0.05em' }}>GAS FILLUP HISTORY</span>
          <button onClick={onRefreshFillups} style={refreshBtnStyle}>REFRESH</button>
        </div>
        {fillups.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '32px', color: '#64748b' }}>
            No gas fillups logged yet
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {fillups.slice(0, 10).map(fillup => (
              <div 
                key={fillup.fillup_id} 
                style={{
                  padding: '12px 16px',
                  backgroundColor: '#020617',
                  borderRadius: '6px',
                  borderLeft: fillup.flag_status === 'flagged' ? '3px solid #ef4444' : 
                              fillup.flag_status === 'pending_details' ? '3px solid #f59e0b' : 
                              '3px solid transparent',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                }}
              >
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ color: '#e2e8f0', fontWeight: '500' }}>{fillup.vendor}</span>
                    {fillup.flag_status === 'flagged' && (
                      <span style={{ 
                        backgroundColor: 'rgba(239, 68, 68, 0.2)', 
                        color: '#ef4444', 
                        padding: '2px 8px', 
                        borderRadius: '4px',
                        fontSize: '0.7rem',
                        fontWeight: 'bold',
                      }}>
                        FLAGGED
                      </span>
                    )}
                  </div>
                  <div style={{ color: '#64748b', fontSize: '0.875rem', display: 'flex', gap: '12px', marginTop: '4px' }}>
                    {fillup.gallons && <span>{fillup.gallons} gal</span>}
                    {fillup.calculated_mpg && <span style={{ color: '#10b981' }}>{fillup.calculated_mpg} MPG</span>}
                    {fillup.odometer && <span>{fillup.odometer.toLocaleString()} mi</span>}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ color: '#f59e0b', fontFamily: "'JetBrains Mono', monospace", fontWeight: 'bold' }}>
                    ${fillup.amount?.toFixed(2)}
                  </div>
                  <div style={{ color: '#64748b', fontSize: '0.75rem' }}>{fillup.date}</div>
                  <div style={{ fontSize: '0.7rem', color: fillup.was_clocked_in ? '#10b981' : '#ef4444' }}>
                    {fillup.was_clocked_in ? 'CLOCKED IN' : 'CLOCKED OUT'}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════════════════════
// FLEET OVERVIEW VIEW (Admin/Manager)
// ═══════════════════════════════════════════════════════════════════════════════

const FleetOverviewView = ({ liveStatus, allEmployees, summary, flags, onRefresh }) => {
  const pendingFlags = flags?.filter(f => f.status === 'pending_review').length || 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Fleet Stats Bar */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '16px' }}>
        <StatCard label="TOTAL MILES" value={summary?.trips?.total_miles?.toFixed(0) || '0'} unit="mi" />
        <StatCard label="GAS SPEND" value={`$${(summary?.gas?.total_cost || 0).toFixed(0)}`} unit="" isMoney />
        <StatCard label="AVG MPG" value={summary?.gas?.average_mpg?.toFixed(1) || '--'} unit="" />
        <StatCard label="RED FLAGS" value={pendingFlags} unit="" isWarning={pendingFlags > 0} />
        <StatCard label="CLOCKED IN" value={liveStatus?.clocked_in_count || 0} unit={`/ ${liveStatus?.total_employees || 0}`} />
      </div>

      {/* Currently Clocked In */}
      <div style={styles.tacticalCard}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <span style={{ fontWeight: 'bold', color: '#e2e8f0', letterSpacing: '0.05em' }}>CURRENTLY CLOCKED IN</span>
          <button onClick={onRefresh} style={refreshBtnStyle}>REFRESH</button>
        </div>
        
        {liveStatus?.clocked_in?.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {liveStatus.clocked_in.map(emp => (
              <div key={emp.user_id} style={{
                padding: '12px 16px',
                backgroundColor: 'rgba(16, 185, 129, 0.1)',
                borderLeft: '3px solid #10b981',
                borderRadius: '6px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: '#10b981',
                    boxShadow: '0 0 8px rgba(16, 185, 129, 0.5)',
                  }} />
                  <span style={{ color: '#e2e8f0', fontWeight: '500' }}>{emp.name}</span>
                </div>
                <div style={{ color: '#94a3b8', fontSize: '0.875rem' }}>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", color: '#10b981' }}>
                    {emp.hours_worked?.toFixed(1)}h
                  </span>
                  {emp.location?.address && (
                    <span style={{ marginLeft: '12px', color: '#64748b' }}>
                      {emp.location.address.substring(0, 30)}...
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '32px', color: '#64748b' }}>
            No employees currently clocked in
          </div>
        )}
      </div>

      {/* All Employees Table */}
      <div style={styles.tacticalCard}>
        <div style={{ fontWeight: 'bold', color: '#e2e8f0', letterSpacing: '0.05em', marginBottom: '16px' }}>
          ALL EMPLOYEES
        </div>
        
        {allEmployees.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                  <th style={tableHeaderStyle}>NAME</th>
                  <th style={tableHeaderStyle}>STATUS</th>
                  <th style={tableHeaderStyle}>GAS THIS MONTH</th>
                  <th style={tableHeaderStyle}>FLAGS</th>
                </tr>
              </thead>
              <tbody>
                {allEmployees.map(emp => (
                  <tr key={emp.user_id} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                    <td style={tableCellStyle}>{emp.name}</td>
                    <td style={tableCellStyle}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                        backgroundColor: emp.is_clocked_in ? 'rgba(16, 185, 129, 0.2)' : 'rgba(100, 116, 139, 0.2)',
                        color: emp.is_clocked_in ? '#10b981' : '#64748b',
                      }}>
                        {emp.is_clocked_in ? 'ON CLOCK' : 'OFF'}
                      </span>
                    </td>
                    <td style={{ ...tableCellStyle, fontFamily: "'JetBrains Mono', monospace", color: '#f59e0b' }}>
                      ${emp.gas_this_month?.toFixed(2) || '0.00'}
                    </td>
                    <td style={tableCellStyle}>
                      {emp.pending_flags > 0 ? (
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          fontWeight: 'bold',
                          backgroundColor: 'rgba(239, 68, 68, 0.2)',
                          color: '#ef4444',
                        }}>
                          {emp.pending_flags}
                        </span>
                      ) : (
                        <span style={{ color: '#64748b' }}>0</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '32px', color: '#64748b' }}>
            No employee data available
          </div>
        )}
      </div>
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════════════════════
// RED FLAGS VIEW
// ═══════════════════════════════════════════════════════════════════════════════

const RedFlagsView = ({ flags, onRefresh, fetchWithAuth, canApprove }) => {
  const [processingId, setProcessingId] = useState(null);

  const handleApprove = async (flagId) => {
    const notes = prompt('Enter approval notes:');
    if (!notes) return;
    
    setProcessingId(flagId);
    try {
      await fetchWithAuth(`/api/mileage/flags/${flagId}/approve`, {
        method: 'POST',
        body: JSON.stringify({ resolution_notes: notes }),
      });
      onRefresh();
    } catch (err) {
      alert(err.message);
    } finally {
      setProcessingId(null);
    }
  };

  const handleDeny = async (flagId) => {
    const notes = prompt('Enter denial reason:');
    if (!notes) return;
    
    setProcessingId(flagId);
    try {
      await fetchWithAuth(`/api/mileage/flags/${flagId}/deny`, {
        method: 'POST',
        body: JSON.stringify({ resolution_notes: notes }),
      });
      onRefresh();
    } catch (err) {
      alert(err.message);
    } finally {
      setProcessingId(null);
    }
  };

  const pendingFlags = flags.filter(f => f.status === 'pending_review');
  const resolvedFlags = flags.filter(f => f.status !== 'pending_review');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontWeight: 'bold', color: '#e2e8f0', letterSpacing: '0.05em', fontSize: '1.25rem' }}>
            RED FLAGS
          </span>
          {pendingFlags.length > 0 && (
            <span style={{
              backgroundColor: '#ef4444',
              color: '#fff',
              padding: '4px 12px',
              borderRadius: '12px',
              fontSize: '0.875rem',
              fontWeight: 'bold',
            }}>
              {pendingFlags.length} pending
            </span>
          )}
        </div>
        <button onClick={onRefresh} style={refreshBtnStyle}>REFRESH</button>
      </div>

      {/* Pending Flags */}
      {pendingFlags.length > 0 && (
        <div style={styles.tacticalCard}>
          <div style={{ fontWeight: 'bold', color: '#ef4444', letterSpacing: '0.05em', marginBottom: '16px' }}>
            PENDING REVIEW
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {pendingFlags.map(flag => (
              <div 
                key={flag.flag_id} 
                style={{
                  padding: '16px',
                  backgroundColor: 'rgba(239, 68, 68, 0.1)',
                  borderLeft: '3px solid #ef4444',
                  borderRadius: '6px',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <span style={{
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '0.7rem',
                        fontWeight: 'bold',
                        backgroundColor: flag.severity === 'high' ? 'rgba(239, 68, 68, 0.3)' :
                                        flag.severity === 'medium' ? 'rgba(245, 158, 11, 0.3)' :
                                        'rgba(100, 116, 139, 0.3)',
                        color: flag.severity === 'high' ? '#ef4444' :
                               flag.severity === 'medium' ? '#f59e0b' : '#94a3b8',
                      }}>
                        {flag.severity?.toUpperCase()}
                      </span>
                      <span style={{ color: '#94a3b8', fontSize: '0.875rem' }}>
                        {flag.flag_type?.replace(/_/g, ' ').toUpperCase()}
                      </span>
                    </div>
                    <div style={{ color: '#e2e8f0', marginBottom: '8px' }}>{flag.description}</div>
                    <div style={{ color: '#64748b', fontSize: '0.875rem', display: 'flex', gap: '16px' }}>
                      {flag.amount && <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>${flag.amount.toFixed(2)}</span>}
                      <span>{flag.date}</span>
                      {flag.employee_name && <span>Employee: {flag.employee_name}</span>}
                    </div>
                    {flag.employee_explanation && (
                      <div style={{
                        marginTop: '12px',
                        padding: '8px 12px',
                        backgroundColor: 'rgba(15, 23, 42, 0.5)',
                        borderRadius: '4px',
                      }}>
                        <span style={{ color: '#94a3b8', fontSize: '0.75rem' }}>EMPLOYEE EXPLANATION:</span>
                        <p style={{ color: '#e2e8f0', fontSize: '0.875rem', marginTop: '4px' }}>{flag.employee_explanation}</p>
                      </div>
                    )}
                  </div>
                  {canApprove && (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => handleApprove(flag.flag_id)}
                        disabled={processingId === flag.flag_id}
                        style={{
                          padding: '8px 16px',
                          borderRadius: '6px',
                          border: 'none',
                          backgroundColor: 'rgba(16, 185, 129, 0.2)',
                          color: '#10b981',
                          fontWeight: 'bold',
                          fontSize: '0.75rem',
                          cursor: processingId === flag.flag_id ? 'not-allowed' : 'pointer',
                          opacity: processingId === flag.flag_id ? 0.5 : 1,
                        }}
                        title="Approve (legitimate expense)"
                      >
                        APPROVE
                      </button>
                      <button
                        onClick={() => handleDeny(flag.flag_id)}
                        disabled={processingId === flag.flag_id}
                        style={{
                          padding: '8px 16px',
                          borderRadius: '6px',
                          border: 'none',
                          backgroundColor: 'rgba(239, 68, 68, 0.2)',
                          color: '#ef4444',
                          fontWeight: 'bold',
                          fontSize: '0.75rem',
                          cursor: processingId === flag.flag_id ? 'not-allowed' : 'pointer',
                          opacity: processingId === flag.flag_id ? 0.5 : 1,
                        }}
                        title="Deny (personal use)"
                      >
                        DENY
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Resolved Flags */}
      {resolvedFlags.length > 0 && (
        <div style={styles.tacticalCard}>
          <div style={{ fontWeight: 'bold', color: '#94a3b8', letterSpacing: '0.05em', marginBottom: '16px' }}>
            RESOLVED
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {resolvedFlags.map(flag => (
              <div 
                key={flag.flag_id} 
                style={{
                  padding: '12px 16px',
                  backgroundColor: '#020617',
                  borderRadius: '6px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ color: '#94a3b8', fontSize: '0.875rem' }}>
                      {flag.flag_type?.replace(/_/g, ' ')}
                    </span>
                    <span style={{
                      padding: '2px 8px',
                      borderRadius: '4px',
                      fontSize: '0.7rem',
                      fontWeight: 'bold',
                      backgroundColor: flag.status === 'approved' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                      color: flag.status === 'approved' ? '#10b981' : '#ef4444',
                    }}>
                      {flag.status?.toUpperCase()}
                    </span>
                  </div>
                  <div style={{ color: '#64748b', fontSize: '0.875rem', marginTop: '4px' }}>
                    {flag.description}
                  </div>
                  {flag.resolution_notes && (
                    <div style={{ color: '#64748b', fontSize: '0.75rem', marginTop: '4px' }}>
                      Note: {flag.resolution_notes}
                    </div>
                  )}
                </div>
                <div style={{ color: '#64748b', fontSize: '0.875rem' }}>{flag.date}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {flags.length === 0 && (
        <div style={{ ...styles.tacticalCard, textAlign: 'center', padding: '48px' }}>
          <div style={{ color: '#10b981', fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '8px' }}>
            ALL CLEAR
          </div>
          <div style={{ color: '#64748b' }}>No red flags - everything looks good!</div>
        </div>
      )}
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════════════════════
// VEHICLES VIEW
// ═══════════════════════════════════════════════════════════════════════════════

const VehiclesView = ({ vehicles, onRefresh, fetchWithAuth }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newVehicle, setNewVehicle] = useState({
    year: '',
    make: '',
    model: '',
    license_plate: '',
    expected_mpg: '',
    tank_capacity_gallons: ''
  });
  const [saving, setSaving] = useState(false);

  const handleAddVehicle = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await fetchWithAuth('/api/mileage/vehicles', {
        method: 'POST',
        body: JSON.stringify({
          year: parseInt(newVehicle.year),
          make: newVehicle.make,
          model: newVehicle.model,
          license_plate: newVehicle.license_plate || null,
          expected_mpg: newVehicle.expected_mpg ? parseFloat(newVehicle.expected_mpg) : null,
          tank_capacity_gallons: newVehicle.tank_capacity_gallons ? parseFloat(newVehicle.tank_capacity_gallons) : null
        }),
      });
      
      setNewVehicle({ year: '', make: '', model: '', license_plate: '', expected_mpg: '', tank_capacity_gallons: '' });
      setShowAddForm(false);
      onRefresh();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontWeight: 'bold', color: '#e2e8f0', letterSpacing: '0.05em', fontSize: '1.25rem' }}>
          FLEET VEHICLES
        </span>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button onClick={onRefresh} style={refreshBtnStyle}>REFRESH</button>
          <button 
            onClick={() => setShowAddForm(!showAddForm)} 
            style={primaryBtnStyle}
            data-testid="add-vehicle-btn"
          >
            ADD VEHICLE
          </button>
        </div>
      </div>

      {/* Add Form */}
      {showAddForm && (
        <form onSubmit={handleAddVehicle} style={styles.tacticalCard}>
          <div style={{ marginBottom: '16px', fontWeight: 'bold', color: '#e2e8f0', letterSpacing: '0.05em' }}>
            NEW VEHICLE
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
            <div>
              <label style={labelStyle}>YEAR</label>
              <input
                type="number"
                value={newVehicle.year}
                onChange={(e) => setNewVehicle({ ...newVehicle, year: e.target.value })}
                placeholder="2023"
                required
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>MAKE</label>
              <input
                type="text"
                value={newVehicle.make}
                onChange={(e) => setNewVehicle({ ...newVehicle, make: e.target.value })}
                placeholder="Ford"
                required
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>MODEL</label>
              <input
                type="text"
                value={newVehicle.model}
                onChange={(e) => setNewVehicle({ ...newVehicle, model: e.target.value })}
                placeholder="F-150"
                required
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>LICENSE PLATE</label>
              <input
                type="text"
                value={newVehicle.license_plate}
                onChange={(e) => setNewVehicle({ ...newVehicle, license_plate: e.target.value })}
                placeholder="ABC1234"
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>EXPECTED MPG</label>
              <input
                type="number"
                step="0.1"
                value={newVehicle.expected_mpg}
                onChange={(e) => setNewVehicle({ ...newVehicle, expected_mpg: e.target.value })}
                placeholder="22"
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>TANK CAPACITY (GAL)</label>
              <input
                type="number"
                step="0.1"
                value={newVehicle.tank_capacity_gallons}
                onChange={(e) => setNewVehicle({ ...newVehicle, tank_capacity_gallons: e.target.value })}
                placeholder="23"
                style={inputStyle}
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
            <button type="submit" disabled={saving} style={primaryBtnStyle}>
              {saving ? 'SAVING...' : 'ADD VEHICLE'}
            </button>
            <button type="button" onClick={() => setShowAddForm(false)} style={secondaryBtnStyle}>
              CANCEL
            </button>
          </div>
        </form>
      )}

      {/* Vehicles Grid */}
      {vehicles.length === 0 ? (
        <div style={{ ...styles.tacticalCard, textAlign: 'center', padding: '48px' }}>
          <div style={{ color: '#64748b' }}>No vehicles added yet</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
          {vehicles.map(vehicle => (
            <div key={vehicle.vehicle_id} style={styles.tacticalCard}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ color: '#e2e8f0', fontWeight: 'bold', fontSize: '1.1rem' }}>
                    {vehicle.year} {vehicle.make} {vehicle.model}
                  </div>
                  <div style={{ color: '#64748b', fontSize: '0.875rem', marginTop: '8px', display: 'flex', gap: '16px' }}>
                    {vehicle.license_plate && <span>Plate: {vehicle.license_plate}</span>}
                    {vehicle.expected_mpg && <span>Expected: {vehicle.expected_mpg} MPG</span>}
                  </div>
                </div>
                <span style={{
                  padding: '4px 12px',
                  borderRadius: '4px',
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                  backgroundColor: vehicle.status === 'active' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(100, 116, 139, 0.2)',
                  color: vehicle.status === 'active' ? '#10b981' : '#94a3b8',
                }}>
                  {vehicle.status?.toUpperCase()}
                </span>
              </div>
              {vehicle.current_odometer && (
                <div style={{
                  marginTop: '12px',
                  paddingTop: '12px',
                  borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                  color: '#94a3b8',
                  fontSize: '0.875rem',
                }}>
                  Odometer: <span style={{ fontFamily: "'JetBrains Mono', monospace", color: '#e2e8f0' }}>
                    {vehicle.current_odometer.toLocaleString()} mi
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════════════════════
// REPORTS VIEW
// ═══════════════════════════════════════════════════════════════════════════════

const ReportsView = ({ summary, fetchWithAuth }) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Summary Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
        <StatCard label="TOTAL GAS COST" value={`$${(summary?.gas?.total_cost || 0).toFixed(0)}`} unit="" isMoney />
        <StatCard label="TOTAL GALLONS" value={(summary?.gas?.total_gallons || 0).toFixed(0)} unit="gal" />
        <StatCard label="AVERAGE MPG" value={summary?.gas?.average_mpg?.toFixed(1) || '--'} unit="" />
        <StatCard label="TOTAL MILES" value={(summary?.trips?.total_miles || 0).toFixed(0)} unit="mi" />
      </div>

      {/* Period Info */}
      <div style={styles.tacticalCard}>
        <div style={{ fontWeight: 'bold', color: '#e2e8f0', letterSpacing: '0.05em', marginBottom: '16px' }}>
          PERIOD SUMMARY
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px' }}>
          <div>
            <div style={{ color: '#94a3b8', fontSize: '0.75rem', letterSpacing: '0.05em', marginBottom: '8px' }}>
              GAS STATISTICS
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#64748b' }}>Total Fill-ups</span>
                <span style={{ color: '#e2e8f0', fontFamily: "'JetBrains Mono', monospace" }}>
                  {summary?.gas?.fillup_count || 0}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#64748b' }}>Total Gallons</span>
                <span style={{ color: '#e2e8f0', fontFamily: "'JetBrains Mono', monospace" }}>
                  {(summary?.gas?.total_gallons || 0).toFixed(1)}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#64748b' }}>Total Cost</span>
                <span style={{ color: '#f59e0b', fontFamily: "'JetBrains Mono', monospace", fontWeight: 'bold' }}>
                  ${(summary?.gas?.total_cost || 0).toFixed(2)}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#64748b' }}>Average MPG</span>
                <span style={{ color: '#10b981', fontFamily: "'JetBrains Mono', monospace", fontWeight: 'bold' }}>
                  {summary?.gas?.average_mpg?.toFixed(1) || '--'}
                </span>
              </div>
            </div>
          </div>
          <div>
            <div style={{ color: '#94a3b8', fontSize: '0.75rem', letterSpacing: '0.05em', marginBottom: '8px' }}>
              TRIP STATISTICS
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#64748b' }}>Total Trips</span>
                <span style={{ color: '#e2e8f0', fontFamily: "'JetBrains Mono', monospace" }}>
                  {summary?.trips?.trip_count || 0}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#64748b' }}>Total Miles</span>
                <span style={{ color: '#e2e8f0', fontFamily: "'JetBrains Mono', monospace" }}>
                  {(summary?.trips?.total_miles || 0).toFixed(1)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Flags Summary */}
      <div style={styles.tacticalCard}>
        <div style={{ fontWeight: 'bold', color: '#e2e8f0', letterSpacing: '0.05em', marginBottom: '16px' }}>
          RED FLAGS SUMMARY
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
          <div style={{
            padding: '16px',
            backgroundColor: summary?.flags?.pending > 0 ? 'rgba(239, 68, 68, 0.1)' : 'rgba(16, 185, 129, 0.1)',
            borderLeft: `3px solid ${summary?.flags?.pending > 0 ? '#ef4444' : '#10b981'}`,
            borderRadius: '6px',
          }}>
            <div style={{ color: '#94a3b8', fontSize: '0.75rem', letterSpacing: '0.05em' }}>PENDING</div>
            <div style={{
              fontSize: '2rem',
              fontWeight: 'bold',
              fontFamily: "'Rajdhani', sans-serif",
              color: summary?.flags?.pending > 0 ? '#ef4444' : '#10b981',
            }}>
              {summary?.flags?.pending || 0}
            </div>
          </div>
          <div style={{
            padding: '16px',
            backgroundColor: 'rgba(100, 116, 139, 0.1)',
            borderLeft: '3px solid #64748b',
            borderRadius: '6px',
          }}>
            <div style={{ color: '#94a3b8', fontSize: '0.75rem', letterSpacing: '0.05em' }}>THIS PERIOD</div>
            <div style={{
              fontSize: '2rem',
              fontWeight: 'bold',
              fontFamily: "'Rajdhani', sans-serif",
              color: '#e2e8f0',
            }}>
              {summary?.flags?.total_this_period || 0}
            </div>
          </div>
        </div>
      </div>

      {/* Fleet Info */}
      <div style={styles.tacticalCard}>
        <div style={{ fontWeight: 'bold', color: '#e2e8f0', letterSpacing: '0.05em', marginBottom: '16px' }}>
          FLEET INFO
        </div>
        <div style={{ display: 'flex', gap: '32px' }}>
          <div>
            <div style={{ color: '#94a3b8', fontSize: '0.75rem', letterSpacing: '0.05em' }}>ACTIVE VEHICLES</div>
            <div style={{
              fontSize: '1.5rem',
              fontWeight: 'bold',
              fontFamily: "'Rajdhani', sans-serif",
              color: '#10b981',
            }}>
              {summary?.fleet?.active_vehicles || 0}
            </div>
          </div>
          <div>
            <div style={{ color: '#94a3b8', fontSize: '0.75rem', letterSpacing: '0.05em' }}>EMPLOYEES WITH ACTIVITY</div>
            <div style={{
              fontSize: '1.5rem',
              fontWeight: 'bold',
              fontFamily: "'Rajdhani', sans-serif",
              color: '#10b981',
            }}>
              {summary?.fleet?.employees_with_activity || 0}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════════════════════
// HELPER COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════

const StatCard = ({ label, value, unit, isMoney, isWarning }) => (
  <div style={{
    ...styles.tacticalCard,
    padding: '16px 20px',
  }}>
    <div style={{ color: '#94a3b8', fontSize: '0.7rem', letterSpacing: '0.1em', marginBottom: '4px' }}>
      {label}
    </div>
    <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
      <span style={{
        fontFamily: isMoney ? "'JetBrains Mono', monospace" : "'Rajdhani', sans-serif",
        fontSize: '1.75rem',
        fontWeight: 'bold',
        color: isWarning ? '#ef4444' : '#10b981',
        textShadow: isWarning ? '0 0 20px rgba(239, 68, 68, 0.4)' : '0 0 20px rgba(16, 185, 129, 0.4)',
      }}>
        {value}
      </span>
      {unit && <span style={{ color: '#64748b', fontSize: '0.875rem' }}>{unit}</span>}
    </div>
  </div>
);


// ═══════════════════════════════════════════════════════════════════════════════
// SHARED STYLES
// ═══════════════════════════════════════════════════════════════════════════════

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  backgroundColor: '#020617',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  borderRadius: '6px',
  color: '#e2e8f0',
  fontSize: '0.875rem',
  outline: 'none',
};

const labelStyle = {
  display: 'block',
  color: '#94a3b8',
  fontSize: '0.75rem',
  marginBottom: '4px',
  letterSpacing: '0.05em',
};

const primaryBtnStyle = {
  padding: '10px 20px',
  borderRadius: '6px',
  border: 'none',
  backgroundColor: '#10b981',
  color: '#ffffff',
  fontWeight: 'bold',
  fontSize: '0.8rem',
  letterSpacing: '0.05em',
  cursor: 'pointer',
};

const secondaryBtnStyle = {
  padding: '10px 20px',
  borderRadius: '6px',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  backgroundColor: 'transparent',
  color: '#94a3b8',
  fontWeight: '500',
  fontSize: '0.8rem',
  cursor: 'pointer',
};

const refreshBtnStyle = {
  padding: '8px 16px',
  borderRadius: '6px',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  backgroundColor: 'transparent',
  color: '#64748b',
  fontWeight: '500',
  fontSize: '0.75rem',
  letterSpacing: '0.05em',
  cursor: 'pointer',
};

const tableHeaderStyle = {
  padding: '12px 16px',
  textAlign: 'left',
  color: '#94a3b8',
  fontSize: '0.7rem',
  letterSpacing: '0.1em',
  fontWeight: '600',
};

const tableCellStyle = {
  padding: '12px 16px',
  color: '#e2e8f0',
  fontSize: '0.875rem',
};


// ═══════════════════════════════════════════════════════════════════════════════
// MILEAGE RULES VIEW
// ═══════════════════════════════════════════════════════════════════════════════

const MileageRulesView = ({ fetchWithAuth }) => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newRule, setNewRule] = useState({
    rule_name: '',
    rule_type: 'mpg_threshold',
    trigger_type: 'below',
    trigger_value: '',
    action: 'flag',
    severity: 'medium',
    description: ''
  });

  const RULE_TYPES = [
    { id: 'mpg_threshold', label: 'MPG THRESHOLD', desc: 'Flag when MPG is too low or too high' },
    { id: 'spending_limit', label: 'SPENDING LIMIT', desc: 'Flag when gas spending exceeds limit' },
    { id: 'clock_violation', label: 'CLOCK VIOLATION', desc: 'Flag gas purchases while clocked out' },
    { id: 'trip_frequency', label: 'TRIP FREQUENCY', desc: 'Flag excessive trips in a day' }
  ];

  const TRIGGER_TYPES = [
    { id: 'below', label: 'Below' },
    { id: 'above', label: 'Above' },
    { id: 'equal', label: 'Equal to' }
  ];

  const ACTIONS = [
    { id: 'flag', label: 'Create Red Flag' },
    { id: 'notify', label: 'Notify Admin' },
    { id: 'auto_deny', label: 'Auto-Deny' }
  ];

  const loadRules = useCallback(async () => {
    try {
      const data = await fetchWithAuth('/api/mileage/rules');
      setRules(data.rules || []);
    } catch (err) {
      console.error('Failed to load rules:', err);
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth]);

  useEffect(() => {
    loadRules();
  }, [loadRules]);

  const handleAddRule = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await fetchWithAuth('/api/mileage/rules', {
        method: 'POST',
        body: JSON.stringify({
          ...newRule,
          trigger_value: newRule.trigger_value ? parseFloat(newRule.trigger_value) : null
        }),
      });
      
      setNewRule({
        rule_name: '',
        rule_type: 'mpg_threshold',
        trigger_type: 'below',
        trigger_value: '',
        action: 'flag',
        severity: 'medium',
        description: ''
      });
      setShowAddForm(false);
      loadRules();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleRule = async (ruleId) => {
    try {
      await fetchWithAuth(`/api/mileage/rules/${ruleId}/toggle`, { method: 'POST' });
      loadRules();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!confirm('Delete this rule?')) return;
    try {
      await fetchWithAuth(`/api/mileage/rules/${ruleId}`, { method: 'DELETE' });
      loadRules();
    } catch (err) {
      alert(err.message);
    }
  };

  const activeCount = rules.filter(r => r.is_active).length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
        <StatCard label="TOTAL RULES" value={rules.length} unit="" />
        <StatCard label="ACTIVE" value={activeCount} unit="" />
        <StatCard label="TRIGGERED TODAY" value={rules.reduce((sum, r) => sum + (r.match_count || 0), 0)} unit="" />
      </div>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontWeight: 'bold', color: '#e2e8f0', letterSpacing: '0.05em', fontSize: '1.25rem' }}>
          MILEAGE RULES
        </span>
        <button 
          onClick={() => setShowAddForm(!showAddForm)} 
          style={primaryBtnStyle}
          data-testid="add-rule-btn"
        >
          ADD RULE
        </button>
      </div>

      {/* Add Form */}
      {showAddForm && (
        <form onSubmit={handleAddRule} style={styles.tacticalCard}>
          <div style={{ marginBottom: '16px', fontWeight: 'bold', color: '#e2e8f0', letterSpacing: '0.05em' }}>
            NEW RULE
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div style={{ gridColumn: 'span 2' }}>
              <label style={labelStyle}>RULE NAME</label>
              <input
                type="text"
                value={newRule.rule_name}
                onChange={(e) => setNewRule({ ...newRule, rule_name: e.target.value })}
                placeholder="e.g., Low MPG Alert"
                required
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>RULE TYPE</label>
              <select
                value={newRule.rule_type}
                onChange={(e) => setNewRule({ ...newRule, rule_type: e.target.value })}
                style={inputStyle}
              >
                {RULE_TYPES.map(rt => (
                  <option key={rt.id} value={rt.id}>{rt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle}>TRIGGER</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <select
                  value={newRule.trigger_type}
                  onChange={(e) => setNewRule({ ...newRule, trigger_type: e.target.value })}
                  style={{ ...inputStyle, flex: 1 }}
                >
                  {TRIGGER_TYPES.map(tt => (
                    <option key={tt.id} value={tt.id}>{tt.label}</option>
                  ))}
                </select>
                <input
                  type="number"
                  step="0.1"
                  value={newRule.trigger_value}
                  onChange={(e) => setNewRule({ ...newRule, trigger_value: e.target.value })}
                  placeholder="Value"
                  style={{ ...inputStyle, flex: 1 }}
                />
              </div>
            </div>
            <div>
              <label style={labelStyle}>ACTION</label>
              <select
                value={newRule.action}
                onChange={(e) => setNewRule({ ...newRule, action: e.target.value })}
                style={inputStyle}
              >
                {ACTIONS.map(a => (
                  <option key={a.id} value={a.id}>{a.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle}>SEVERITY</label>
              <select
                value={newRule.severity}
                onChange={(e) => setNewRule({ ...newRule, severity: e.target.value })}
                style={inputStyle}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
            <div style={{ gridColumn: 'span 2' }}>
              <label style={labelStyle}>DESCRIPTION (OPTIONAL)</label>
              <input
                type="text"
                value={newRule.description}
                onChange={(e) => setNewRule({ ...newRule, description: e.target.value })}
                placeholder="Description of what this rule does"
                style={inputStyle}
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
            <button type="submit" disabled={saving} style={primaryBtnStyle}>
              {saving ? 'SAVING...' : 'CREATE RULE'}
            </button>
            <button type="button" onClick={() => setShowAddForm(false)} style={secondaryBtnStyle}>
              CANCEL
            </button>
          </div>
        </form>
      )}

      {/* Rules List */}
      <div style={styles.tacticalCard}>
        <div style={{ marginBottom: '16px', color: '#94a3b8', fontSize: '0.875rem' }}>
          Rules automatically flag transactions based on thresholds. Configure MPG limits, spending caps, and violation alerts.
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '32px', color: '#64748b' }}>Loading...</div>
        ) : rules.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px' }}>
            <div style={{ color: '#64748b', marginBottom: '8px' }}>No rules configured yet</div>
            <div style={{ color: '#94a3b8', fontSize: '0.875rem' }}>Add rules to automatically flag suspicious activity</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {rules.map(rule => {
              const ruleType = RULE_TYPES.find(rt => rt.id === rule.rule_type);
              return (
                <div 
                  key={rule.rule_id}
                  style={{
                    padding: '16px',
                    backgroundColor: '#020617',
                    borderRadius: '6px',
                    borderLeft: `3px solid ${rule.is_active ? '#10b981' : '#64748b'}`,
                    opacity: rule.is_active ? 1 : 0.6,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <span style={{ color: '#e2e8f0', fontWeight: 'bold' }}>{rule.rule_name}</span>
                        <span style={{
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '0.7rem',
                          fontWeight: 'bold',
                          backgroundColor: rule.severity === 'high' ? 'rgba(239, 68, 68, 0.2)' :
                                          rule.severity === 'medium' ? 'rgba(245, 158, 11, 0.2)' :
                                          'rgba(100, 116, 139, 0.2)',
                          color: rule.severity === 'high' ? '#ef4444' :
                                 rule.severity === 'medium' ? '#f59e0b' : '#94a3b8',
                        }}>
                          {rule.severity?.toUpperCase()}
                        </span>
                      </div>
                      <div style={{ color: '#94a3b8', fontSize: '0.875rem' }}>
                        {ruleType?.label}: {rule.trigger_type} {rule.trigger_value}
                      </div>
                      <div style={{ color: '#64748b', fontSize: '0.75rem', marginTop: '4px' }}>
                        {rule.match_count || 0} matches • Action: {rule.action}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => handleToggleRule(rule.rule_id)}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '4px',
                          border: 'none',
                          backgroundColor: rule.is_active ? 'rgba(16, 185, 129, 0.2)' : 'rgba(100, 116, 139, 0.2)',
                          color: rule.is_active ? '#10b981' : '#94a3b8',
                          fontWeight: 'bold',
                          fontSize: '0.75rem',
                          cursor: 'pointer',
                        }}
                      >
                        {rule.is_active ? 'ACTIVE' : 'DISABLED'}
                      </button>
                      <button
                        onClick={() => handleDeleteRule(rule.rule_id)}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '4px',
                          border: 'none',
                          backgroundColor: 'rgba(239, 68, 68, 0.1)',
                          color: '#ef4444',
                          fontWeight: 'bold',
                          fontSize: '0.75rem',
                          cursor: 'pointer',
                        }}
                      >
                        DELETE
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Preset Rules */}
      <div style={styles.tacticalCard}>
        <div style={{ fontWeight: 'bold', color: '#e2e8f0', letterSpacing: '0.05em', marginBottom: '16px' }}>
          COMMON RULE PRESETS
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
          {[
            { name: 'Low MPG Alert', type: 'mpg_threshold', trigger: 'below', value: 12, desc: 'Flag when MPG drops below 12' },
            { name: 'High Gas Spend', type: 'spending_limit', trigger: 'above', value: 100, desc: 'Flag fillups over $100' },
            { name: 'Weekend Purchase', type: 'clock_violation', trigger: 'equal', value: 0, desc: 'Flag gas bought while off clock' },
            { name: 'High MPG Alert', type: 'mpg_threshold', trigger: 'above', value: 40, desc: 'Flag suspiciously high MPG' }
          ].map((preset, idx) => (
            <button
              key={idx}
              onClick={async () => {
                try {
                  await fetchWithAuth('/api/mileage/rules', {
                    method: 'POST',
                    body: JSON.stringify({
                      rule_name: preset.name,
                      rule_type: preset.type,
                      trigger_type: preset.trigger,
                      trigger_value: preset.value,
                      action: 'flag',
                      severity: 'medium',
                      description: preset.desc
                    }),
                  });
                  loadRules();
                } catch (err) {
                  alert(err.message);
                }
              }}
              style={{
                padding: '12px 16px',
                backgroundColor: '#020617',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '6px',
                textAlign: 'left',
                cursor: 'pointer',
              }}
            >
              <div style={{ color: '#e2e8f0', fontWeight: '500', marginBottom: '4px' }}>{preset.name}</div>
              <div style={{ color: '#64748b', fontSize: '0.75rem' }}>{preset.desc}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};


export default MileageTab;
